import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import { AIAssistantProvider } from "./context/AIAssistantContext";
import AIPageContextBridge from "./components/AIPageContextBridge";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConnectivityProvider } from "./context/ConnectivityContext";
import OfflinePosRuntime from "./components/OfflinePosRuntime";
import AuditLogs from "./pages/AuditLogs";
import Customers from "./pages/Customers";
import Conversations from "./pages/Conversations";
import Dashboard from "./pages/Dashboard";
import Documents from "./pages/Documents";
import EmployeeOperations from "./pages/EmployeeOperations";
import Inventory from "./pages/Inventory";
import LandingPage from "./pages/LandingPage";
import LegalAcceptance from "./pages/LegalAcceptance";
import LegalDocument from "./pages/LegalDocument";
import Notifications from "./pages/Notifications";
import Orders from "./pages/Orders";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import HardwareSetup from "./pages/HardwareSetup";
import ResetPassword from "./pages/ResetPassword";
import OnboardingStatus from "./pages/OnboardingStatus";
import POSIntegrations from "./pages/POSIntegrations";
import PlatformOperations from "./pages/PlatformOperations";
import PlatformAIUsage from "./pages/PlatformAIUsage";
import PointOfSale from "./pages/PointOfSale";
import PricingPolicy from "./pages/PricingPolicy";
import Quotations from "./pages/Quotations";
import Products from "./pages/Products";
import PurchaseInvoices from "./pages/PurchaseInvoices";
import PurchaseOrderDetail from "./pages/PurchaseOrderDetail";
import PurchaseOrders from "./pages/PurchaseOrders";
import PaymentsExpenses from "./pages/PaymentsExpenses";
import Register from "./pages/Register";
import Reports from "./pages/Reports";
import Sales from "./pages/Sales";
import Settings from "./pages/Settings";
import StoreImport from "./pages/StoreImport";
import StoreProfile from "./pages/StoreProfile";
import WhatsAppIntegration from "./pages/WhatsAppIntegration";
import WorkspaceMembers from "./pages/WorkspaceMembers";


function HomeRedirect() {
  const { contextReady, platformRole, workspaceOperational, legalAcceptanceRequired } = useAuth();
  if (platformRole) return <Navigate to="/platform" replace />;
  if (legalAcceptanceRequired) return <Navigate to="/legal/accept" replace />;
  if (contextReady && workspaceOperational) {
    return <Navigate to="/dashboard" replace />;
  }
  if (contextReady) return <Navigate to="/onboarding" replace />;
  return <Navigate to="/settings" replace />;
}

function Secure({
  children,
  roles,
  platformRoles,
  requireTenantContext = true,
  requireLegalAcceptance = true,
}) {
  return (
    <ProtectedRoute
      roles={roles}
      platformRoles={platformRoles}
      requireTenantContext={requireTenantContext}
      requireLegalAcceptance={requireLegalAcceptance}
    >
      {children}
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <ConnectivityProvider>
      <AuthProvider>
        <OfflinePosRuntime />
        <BrowserRouter>
          <AIAssistantProvider>
          <AIPageContextBridge />
          <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/terms" element={<LegalDocument documentType="terms" />} />
          <Route path="/privacy" element={<LegalDocument documentType="privacy" />} />
          <Route
            path="/legal/accept"
            element={
              <Secure requireTenantContext={false} requireLegalAcceptance={false}>
                <LegalAcceptance />
              </Secure>
            }
          />
          <Route
            path="/onboarding"
            element={<Secure requireTenantContext={false}><OnboardingStatus /></Secure>}
          />

          <Route path="/dashboard" element={<Secure><Dashboard /></Secure>} />
          <Route path="/products" element={<Secure><Products /></Secure>} />
          <Route path="/purchase-invoices" element={<Secure roles={["owner", "admin"]}><PurchaseInvoices /></Secure>} />
          <Route path="/purchase-orders" element={<Secure roles={["owner", "admin"]}><PurchaseOrders /></Secure>} />
          <Route path="/purchase-orders/:id" element={<Secure roles={["owner", "admin"]}><PurchaseOrderDetail /></Secure>} />
          <Route path="/payments-expenses" element={<Secure roles={["owner", "admin"]}><PaymentsExpenses /></Secure>} />
          <Route path="/pos" element={<Secure roles={["owner", "admin", "staff"]}><PointOfSale /></Secure>} />
          <Route path="/hardware" element={<Secure roles={["owner", "admin", "staff"]}><HardwareSetup /></Secure>} />
          <Route path="/inventory" element={<Secure><Inventory /></Secure>} />
          <Route path="/customers" element={<Secure><Customers /></Secure>} />
          <Route path="/conversations" element={<Secure><Conversations /></Secure>} />
          <Route path="/notifications" element={<Secure><Notifications /></Secure>} />
          <Route path="/employee-operations" element={<Secure roles={["owner", "admin", "staff"]}><EmployeeOperations /></Secure>} />
          <Route path="/quotations" element={<Secure><Quotations /></Secure>} />
          <Route path="/orders" element={<Secure><Orders /></Secure>} />
          <Route path="/checkout-settings" element={<Navigate to="/store-profile" replace />} />
          <Route path="/sales" element={<Secure roles={["owner", "admin", "staff"]}><Sales /></Secure>} />
          <Route path="/reports" element={<Secure roles={["owner"]}><Reports /></Secure>} />
          <Route path="/pricing" element={<Secure roles={["owner", "admin"]}><PricingPolicy /></Secure>} />
          <Route path="/team" element={<Secure roles={["owner"]}><WorkspaceMembers /></Secure>} />
          <Route path="/documents" element={<Secure><Documents /></Secure>} />
          <Route
            path="/ai"
            element={<Secure><Navigate to="/dashboard" replace state={{ openAssistant: true }} /></Secure>}
          />
          <Route
            path="/whatsapp"
            element={<Secure roles={["owner", "admin"]}><WhatsAppIntegration /></Secure>}
          />
          <Route
            path="/pos-integrations"
            element={<Secure roles={["owner", "admin"]}><POSIntegrations /></Secure>}
          />
          <Route
            path="/audit-logs"
            element={<Secure roles={["owner", "admin"]}><AuditLogs /></Secure>}
          />
          <Route
            path="/platform"
            element={
              <Secure
                platformRoles={["super_admin", "operations", "support", "read_only"]}
                requireTenantContext={false}
                requireLegalAcceptance={false}
              >
                <PlatformOperations />
              </Secure>
            }
          />
          <Route
            path="/platform/ai-usage"
            element={
              <Secure
                platformRoles={["super_admin", "operations", "support", "read_only"]}
                requireTenantContext={false}
                requireLegalAcceptance={false}
              >
                <PlatformAIUsage />
              </Secure>
            }
          />
          <Route path="/store-import" element={<Secure roles={["owner", "admin"]}><StoreImport /></Secure>} />
          <Route path="/store-profile" element={<Secure roles={["owner", "admin"]}><StoreProfile /></Secure>} />
          <Route path="/settings" element={<Secure requireTenantContext={false}><Settings /></Secure>} />
          <Route path="*" element={<HomeRedirect />} />
          </Routes>
          </AIAssistantProvider>
        </BrowserRouter>
      </AuthProvider>
    </ConnectivityProvider>
  );
}
